/* eslint-disable react/no-danger */
import { useEffect, useState, useMemo } from 'preact/hooks'
import { useSubscriptionPlans, usePurchaseOptions } from 'hooks/useProductSubscription'
import { PURCHASE_OPTIONS, PURCHASE_OPTION_LABELS } from 'helpers/constants'
import { openModal } from 'helpers/dom'
import CustomDropdown from 'snippets/form/custom-dropdown/custom-dropdown'
import CheckboxInput from 'snippets/form/checkbox-input/checkbox-input'
import Icon from 'snippets/icon/icon'

function SubscriptionOptionLabel({ savings = '' }) {
  return (
    <div className="text-xs text-neutral-900">{PURCHASE_OPTION_LABELS[PURCHASE_OPTIONS.SUBSCRIPTION]} {savings}</div>
  )
}

function ProductSubscription({
  planGroups,
  setSellingPlanId,
  currentVariant,
  setCurrentVariantPrice,
  isBundleDynamic,
  bundles,
  setIsDisabledATC,
  setCurrentSellingPlan,
  minItemsRequired,
  setTotalPriceSubscription,
  classWrap
}) {
  if (!planGroups?.length) return <div className="mt-4" />

  const titleSubscription = window.productState?.titleSubscription || ''
  const { availablePlanGroups, selectedPlan, setSelectedPlan, hasAvailablePlans } = useSubscriptionPlans(
    planGroups,
    currentVariant,
    isBundleDynamic
  )

  const {
    selectedPurchaseOption,
    subscriptionPrice,
    subscriptionSavings,
    handlePurchaseOptionChange,
    isSubscriptionSelected
  } = usePurchaseOptions(currentVariant, selectedPlan, setSellingPlanId, setCurrentVariantPrice)

  if (!hasAvailablePlans) return <div className="mt-4" />

  const [renderKey, setRenderKey] = useState(1)
  const [messageError, setMessageError] = useState('')
  const bundleList = bundles ? Object.values(bundles) : []

  const bundleHasSellingPlan = useMemo(
    () =>
      minItemsRequired === bundleList.length &&
      bundleList.length > 0 &&
      bundleList.every((bundle) => bundle.product.selling_plan_groups?.length > 0),
    [bundles]
  )

  useEffect(() => {
    if (!selectedPlan) return

    const isValid = currentVariant?.sellingPlanAllocations?.some(
      (allocation) => allocation?.sellingPlanGroupId === selectedPlan.id
    )

    if (!isValid) {
      setSelectedPlan(null)
      setRenderKey((prev) => prev + 1)
    }
  }, [currentVariant])

  const purchaseOptions = [
    {
      value: PURCHASE_OPTIONS.SUBSCRIPTION,
      label: <SubscriptionOptionLabel savings={subscriptionSavings} />,
      price: subscriptionPrice
    }
  ]

  const handleSubscriptionBundle = () => {
    if (!bundleHasSellingPlan) return { price: 0, error: null, sellingPlan: null }

    const currentSellingPlan = selectedPlan?.options[1]?.values?.[0]

    if (!currentSellingPlan) return { price: 0, error: null, sellingPlan: null }

    const invalidBundle = bundleList.find(
      (bundle) =>
        !bundle.product.selling_plan_groups.some((plan) => plan.options[1]?.values?.[0] === currentSellingPlan)
    )

    if (invalidBundle) {
      return {
        price: 0,
        error: `Product "${invalidBundle.product.title}" does not have the plan "${currentSellingPlan}"`,
        sellingPlan: null
      }
    }

    const totalPrice = bundleList.reduce((total, bundle) => {
      const sellingPlan = bundle.product.selling_plan_groups.find(
        (plan) => plan.options[1]?.values?.[0] === currentSellingPlan
      )

      if (!sellingPlan) return total

      const variant = bundle.product.variants?.[0]

      if (!variant) return total

      const allocation = variant.selling_plan_allocations.find((item) => item.selling_plan_group_id === sellingPlan.id)

      return allocation ? total + allocation.price : total
    }, 0)

    return { price: totalPrice, error: null, sellingPlan: currentSellingPlan }
  }

  const {
    price: subscriptionBundlePrice,
    error,
    sellingPlan
  } = useMemo(() => handleSubscriptionBundle(), [bundleList, selectedPlan, bundleHasSellingPlan])

  useEffect(() => {
    if (!isBundleDynamic) return

    if (isSubscriptionSelected) {
      setTotalPriceSubscription(subscriptionBundlePrice)
      setMessageError(error || '')
      setIsDisabledATC(!!error)
      setCurrentSellingPlan(sellingPlan)
    } else {
      setTotalPriceSubscription(0)
      setMessageError('')
      setIsDisabledATC(false)
      setCurrentSellingPlan(null)
    }
  }, [isSubscriptionSelected, subscriptionBundlePrice, error, sellingPlan])

  return (
    <div className={`flex flex-col px-4 py-3 border-x border-neutral-900 ${classWrap}`} role="group">
      <div className="flex flex-col gap-4">
        {purchaseOptions.map((option) => (
          <div className="flex items-center gap-2.5" key={option.value}>
            <CheckboxInput
              value={option.value}
              checked={option.value === selectedPurchaseOption}
              name="purchase-option"
              label={option.label}
              onSelected={handlePurchaseOptionChange}
              classWrap={`!mb-0 ${isBundleDynamic && option.value === PURCHASE_OPTIONS.SUBSCRIPTION && !bundleHasSellingPlan ? 'pointer-events-none opacity-50' : ''}`}
              aria-describedby={option.value === PURCHASE_OPTIONS.SUBSCRIPTION ? 'subscription-details' : undefined}
            />
            {titleSubscription && (
              <button type="button" onClick={() => openModal('modal-product-subscriptions')} aria-label={titleSubscription}>
                <Icon
                  name="icon-info"
                  viewBox="0 0 24 24"
                  className="size-3.5 text-neutral-700"
                />
              </button>
            )}
          </div>
        ))}
      </div>

      {isSubscriptionSelected && (
        <div className="mt-4" id="subscription-details">
          <CustomDropdown
            key={`subscription-plans-${availablePlanGroups.length}-${renderKey}`}
            items={availablePlanGroups}
            value={selectedPlan}
            onChange={setSelectedPlan}
            isCustom
            aria-label="Select subscription plan"
            id="subscription-plan-select"
          />
        </div>
      )}

      {messageError && isBundleDynamic ? <span className="mt-1 text-xxs text-red-500">{messageError}</span> : ''}
    </div>
  )
}

export default ProductSubscription
